// Measured line fitting for a flat RDR resume JSON (Oct 25 2026).
// Same server engine as the results page's "Re-check line fit": the summary
// rectangle, highlights (2 full lines) and bullets (1 line) are measured with
// the real Aptos fonts and rewritten until they pass. Used by the Full
// Editor's AI Assistant so every edit still obeys the RDR line rules.
import { supabase } from '@/integrations/supabase/client';
import { applyFits, jobBullets } from '@/lib/resumeFit';
import { balanceSkillRows } from '@/lib/resumeLayoutQa';
import { ledgerRunFields } from '@/lib/ledgerRun';

const FIT_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd';
const FIT_APIKEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I';

type Item = { id: string; kind: 'summary' | 'highlight' | 'pe_bullet'; text: string; chtitle?: string };

function items(flat: any): Item[] {
  const out: Item[] = [];
  const s = String(flat?.Summary ?? '').trim();
  if (s) out.push({ id: 's', kind: 'summary', text: s });
  (flat?.CareerHighlights ?? []).forEach((h: any, i: number) => {
    const t = String(h?.chdescription ?? '').trim();
    if (t) out.push({ id: `h${i}`, kind: 'highlight', text: t, chtitle: String(h?.chtitle ?? '').trim() });
  });
  (flat?.ProfessionalExperience ?? []).forEach((j: any, ji: number) => {
    jobBullets(j).forEach((b: any, bi: number) => {
      const t = String(b?.Description ?? '').trim();
      if (t) out.push({ id: `e${ji}.${bi}`, kind: 'pe_bullet', text: t });
    });
  });
  return out;
}

/**
 * Fit the lines of `flat`. When `before` is given, only lines whose text
 * differs from `before` are measured (an edit only re-fits what it touched).
 */
export async function fitResumeLines(flat: any, before?: any, opts?: { passedOnly?: boolean }): Promise<{ json: any; fixed: number; unresolved: number }> {
  if (!flat || typeof flat !== 'object') return { json: flat, fixed: 0, unresolved: 0 };
  let working = structuredClone(flat);
  for (const [field, prefix] of [['SkillRows', 'Skill'], ['TechnicalSkillRows', 'TechSkill']] as const) {
    const balanced = balanceSkillRows((working as any)[field], prefix);
    if (balanced.changed) (working as any)[field] = balanced.rows;
  }
  const prev = before ? new Map(items(before).map((it) => [`${it.kind}:${it.text}`, true])) : null;
  const todo = items(working).filter((it) => !prev || !prev.has(`${it.kind}:${it.text}`));
  if (todo.length === 0) return { json: working, fixed: 0, unresolved: 0 };

  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (!token) throw new Error('You are not signed in.');
  const body = JSON.stringify({ stage: 6, measure: true, items: todo, ...ledgerRunFields() });
  const call = () => fetch(FIT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: FIT_APIKEY },
    body,
    signal: AbortSignal.timeout(160_000),
  });
  let res = await call();
  if (!res.ok && res.status >= 500) { await new Promise((r) => setTimeout(r, 3000)); res = await call(); }
  if (!res.ok) throw new Error(`line fit HTTP ${res.status}`);
  const j = await res.json();
  if (!j?.success || !Array.isArray(j.items)) throw new Error(j?.error || 'line fit failed');
  const unresolved: Array<{ id: string; text: string }> = Array.isArray(j.unresolved) ? j.unresolved : [];
  // passedOnly: never take a near miss, only wording that measured as fitting.
  const fixes = opts?.passedOnly ? j.items : [...j.items, ...unresolved.filter((u) => u.text && u.text !== todo.find((t) => t.id === u.id)?.text)];
  working = applyFits(working, fixes);
  if (j?.extras?.SummaryBoldPhrase) (working as any).SummaryBoldPhrase = String(j.extras.SummaryBoldPhrase);
  return { json: working, fixed: j.items.length, unresolved: unresolved.length };
}
